/**
 * `POST /sym/seasonality/windows` — a Seasonality screen's rows
 * (`docs/features/seasonality.md`).
 *
 * qv-data reads its yearly store's best windows for the symbols sent (the base
 * screen's rows) and lists the ones a start date and mode select. Same service
 * and base URL as `/sym/*`, and `api/topdown.ts`'s rule: nothing here throws.
 */

import type { ScreenerSeasonality } from "@qufolio/model";

import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const SYM_URL = (
  httpBase(import.meta.env.VITE_QV_SYM_URL, import.meta.env.VITE_QV_SET_URL) || ""
).replace(/\/$/, "");

export const seasonalityConfigured = SYM_URL !== "";

/** One window, as the server sends it. The Pro fields are absent for Basic. */
export interface SeasWindow {
  symbol: string;
  dir: "L" | "S";
  start: number;
  len: number;
  startDate: string | null;
  endDate: string | null;
  /** Trading days from the start date to the window's start; negative before it. */
  offset: number;
  endOffset: number;
  hit: number;
  up: number;
  yearsUsed: number;
  edge: number;
  xsHit?: number;
  xsSharpe?: number;
  winAvg?: number;
  loseAvg?: number;
  sharpe?: number;
  sortino?: number;
  tStat?: number;
  pValue?: number;
  profitF?: number;
  worst?: number;
  var?: number;
  run?: number;
}

export interface WindowsAnswer {
  rows: SeasWindow[];
  truncated: boolean;
  /** The start date used — today or the asked date, rolled to the next session. */
  from: string;
  rolled: boolean;
  asked: string;
  basis: string;
  rank: string;
  pro: boolean;
}

export type WindowsResult = { ok: true; answer: WindowsAnswer } | { ok: false; reason: string };

export async function fetchWindows(
  symbols: readonly string[],
  seas: ScreenerSeasonality,
  signal?: AbortSignal,
): Promise<WindowsResult> {
  if (!SYM_URL) return { ok: false, reason: "no server configured" };
  try {
    const response = await fetch(`${SYM_URL}/sym/seasonality/windows`, {
      method: "POST",
      headers: { ...(await requestHeaders()), "Content-Type": "application/json" },
      body: JSON.stringify({
        symbols,
        mode: seas.mode ?? "soon",
        basis: seas.basis ?? "d",
        years: seas.years ?? "10",
        dir: seas.dir ?? "both",
        rank: seas.rank ?? "season",
        hitMin: seas.hitMin ?? 80,
        avgMin: seas.avgMin ?? 0,
        minYears: seas.minYears ?? 5,
        ...(seas.periodMin ? { periodMin: seas.periodMin } : {}),
        ...(seas.periodMax ? { periodMax: seas.periodMax } : {}),
        within: seas.within ?? 10,
        ...(seas.from ? { from: seas.from } : {}),
      }),
      signal,
    });
    if (!response.ok) {
      return { ok: false, reason: (await response.text()) || `windows failed (${response.status})` };
    }
    return { ok: true, answer: (await response.json()) as WindowsAnswer };
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") return { ok: false, reason: "aborted" };
    return { ok: false, reason: cause instanceof Error ? cause.message : "windows failed" };
  }
}

/** Data Panel › Seasonality: one window described in each past year (Pro). */
export interface SeasDetail {
  symbol: string;
  basis: "d" | "w" | "m";
  years: string;
  dir: "L" | "S";
  len: number;
  from: string;
  to: string | null;
  stats: { yearsUsed: number; up: number; hit: number; edge: number; xsHit?: number; worst?: number; run?: number };
  profile: { len: number; hit: number; avg: number; years: number }[];
  each: { year: number; from: string | null; to: string | null; ret: number }[];
  months: { years: { year: number; values: (number | null)[] }[]; hit: (number | null)[]; avg: (number | null)[] };
}

export type DetailResult = { ok: true; detail: SeasDetail } | { ok: false; reason: string };

export async function fetchDetail(
  ask: { symbol: string; basis: string; years: string; from?: string; len?: number; dir?: string },
  signal?: AbortSignal,
): Promise<DetailResult> {
  if (!SYM_URL) return { ok: false, reason: "no server configured" };
  const q = new URLSearchParams({ symbol: ask.symbol, basis: ask.basis, years: ask.years });
  if (ask.from) q.set("from", ask.from);
  if (ask.len !== undefined) q.set("len", String(ask.len));
  if (ask.dir) q.set("dir", ask.dir);
  try {
    const response = await fetch(`${SYM_URL}/sym/seasonality/detail?${q}`, {
      headers: await requestHeaders(),
      signal,
    });
    if (!response.ok) return { ok: false, reason: (await response.text()) || `detail failed (${response.status})` };
    return { ok: true, detail: (await response.json()) as SeasDetail };
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") return { ok: false, reason: "aborted" };
    return { ok: false, reason: cause instanceof Error ? cause.message : "detail failed" };
  }
}
